import { recordingStorage } from './RecordingStorage';
import { transcriptionService, TranscriptionSegment, TranscriptionSession } from './TranscriptionService';
import { FilteredSegment, FilteringStats } from './ContentFilteringService';
import { audioStorageService } from './AudioStorageService';
import { whisperTranscriptionService, WhisperStatus } from './WhisperTranscriptionService';
import { db } from '../config/firebase';
import { collection, doc, setDoc, getDocs, deleteDoc, query, where, orderBy, Timestamp } from 'firebase/firestore';

export interface SessionMetadata {
  sessionId: string;
  patientId: string;
  providerId: string;
  sessionType: 'initial-consultation' | 'follow-up' | 'therapy' | 'assessment' | 'couples' | 'other';
  sessionFormat?: 'audio' | 'video' | 'screen-share'; // NEW: Track media format
  appointmentReason?: string;
  startTime: Date;
  endTime?: Date;
  duration: number; // seconds
  status: 'active' | 'paused' | 'completed' | 'cancelled';
}

export interface SessionData {
  metadata: SessionMetadata;
  recording?: {
    originalBlob: Blob;
    filteredBlob: Blob;
    originalFileSize: number;
    filteredFileSize: number;
    silenceRemoved: number;
    noiseReduction: boolean;
  };
  // NEW: Video-specific data
  videoRecording?: {
    originalBlob: Blob;
    filteredBlob: Blob;
    originalFileSize: number;
    filteredFileSize: number;
    resolution: string;
    frameRate: number;
    videoBitrate: number;
    audioBitrate: number;
    videoCodec: string;
    audioCodec: string;
    recordingMode: 'camera' | 'screen-share' | 'both';
  };
  transcription?: TranscriptionSession;
  whisperTranscription?: TranscriptionSession;
  transcriptionSource?: 'web-speech' | 'whisper';
  filteredContent?: {
    segments: FilteredSegment[];
    stats: FilteringStats;
    summary: string;
  };
  clinicalNotes?: string;
  recordingId?: string;
  videoRecordingId?: string; // NEW: Separate ID for video storage
  tags?: string[];
}

export interface ActiveSession {
  metadata: SessionMetadata;
  audioBlob?: Blob;
  filteredAudioBlob?: Blob;
  videoBlob?: Blob; // NEW: Video blob
  filteredVideoBlob?: Blob; // NEW: Filtered video blob
  transcription?: TranscriptionSession;
  clinicalNotes: string;
  isRecording: boolean;
  isTranscribing: boolean;
  recordingMetadata?: any;
  videoRecordingMetadata?: any; // NEW: Video-specific metadata
}

interface SessionCallbacks {
  onTranscriptionUpdate?: (segment: TranscriptionSegment) => void;
  onSessionUpdate?: (session: TranscriptionSession) => void;
  onRecordingUpdate?: (isRecording: boolean) => void;
  onError?: (error: string) => void;
  onFilteringUpdate?: (segments: FilteredSegment[], stats: FilteringStats) => void;
  onWhisperStatusUpdate?: (status: WhisperStatus) => void;
}

class SessionService {
  private static instance: SessionService;
  private activeSession: ActiveSession | null = null;
  private sessionHistory: Map<string, SessionData> = new Map();
  private callbacks: SessionCallbacks = {};

  private constructor() {
    this.loadSessionHistory();
  }

  static getInstance(): SessionService {
    if (!SessionService.instance) {
      SessionService.instance = new SessionService();
    }
    return SessionService.instance;
  }

  /**
   * Start a new session with a patient
   */
  async startSession(
    patientId: string,
    providerId: string,
    sessionType: SessionMetadata['sessionType'],
    appointmentReason?: string,
    callbacks?: SessionCallbacks,
    sessionFormat?: 'audio' | 'video' | 'screen-share'
  ): Promise<SessionMetadata> {
    if (this.activeSession) {
      throw new Error('A session is already active. Please end the current session first.');
    }

    this.callbacks = callbacks || {};

    const sessionId = this.generateSessionId();
    const metadata: SessionMetadata = {
      sessionId,
      patientId,
      providerId,
      sessionType,
      sessionFormat: sessionFormat || 'audio', // Default to audio if not specified
      appointmentReason,
      startTime: new Date(),
      duration: 0,
      status: 'active'
    };

    this.activeSession = {
      metadata,
      clinicalNotes: '',
      isRecording: false,
      isTranscribing: false
    };

    return metadata;
  }

  /**
   * Start recording for the active session
   */
  async startRecording(): Promise<boolean> {
    if (!this.activeSession) {
      throw new Error('No active session. Please start a session first.');
    }

    if (this.activeSession.isRecording) {
      return false;
    }

    try {
      // Start transcription with content filtering enabled
      const transcriptionStarted = await transcriptionService.startTranscription(
        this.activeSession.metadata.sessionId,
        {
          onTranscript: (segment) => {
            this.callbacks.onTranscriptionUpdate?.(segment);
          },
          onSessionUpdate: (session) => {
            if (this.activeSession) {
              this.activeSession.transcription = session;
            }
            this.callbacks.onSessionUpdate?.(session);
          },
          onError: (error) => {
            console.error('Transcription error:', error);
            this.callbacks.onError?.(error);
          }
        },
        {
          patientId: this.activeSession.metadata.patientId,
          sessionType: this.activeSession.metadata.sessionType,
          appointmentReason: this.activeSession.metadata.appointmentReason
        }
      );

      if (transcriptionStarted) {
        this.activeSession.isRecording = true;
        this.activeSession.isTranscribing = true;
        this.callbacks.onRecordingUpdate?.(true);
        return true;
      } else {
        throw new Error('Failed to start transcription');
      }
    } catch (error) {
      console.error('Failed to start recording:', error);
      this.callbacks.onError?.(`Failed to start recording: ${error}`);
      return false;
    }
  }

  /**
   * Stop recording for the active session
   */
  async stopRecording(audioBlob: Blob, filteredBlob: Blob, recordingMetadata: any): Promise<void> {
    if (!this.activeSession) {
      throw new Error('No active session');
    }

    if (!this.activeSession.isRecording) {
      return;
    }

    try {
      // Stop transcription and get final session with filtering
      const finalTranscription = await transcriptionService.stopTranscription();

      // Store recording blobs
      this.activeSession.audioBlob = audioBlob;
      this.activeSession.filteredAudioBlob = filteredBlob;
      this.activeSession.recordingMetadata = recordingMetadata;
      this.activeSession.transcription = finalTranscription || undefined;

      this.activeSession.isRecording = false;
      this.activeSession.isTranscribing = false;
      this.callbacks.onRecordingUpdate?.(false);

      // Update filtering callback if available
      if (finalTranscription?.filteredSegments && finalTranscription?.filteringStats) {
        this.callbacks.onFilteringUpdate?.(
          finalTranscription.filteredSegments,
          finalTranscription.filteringStats
        );
      }

    } catch (error) {
      console.error('Failed to stop recording:', error);
      this.callbacks.onError?.(`Failed to stop recording: ${error}`);
      throw error;
    }
  }

  /**
   * Update clinical notes for the active session
   */
  updateClinicalNotes(notes: string): void {
    if (!this.activeSession) {
      throw new Error('No active session');
    }

    this.activeSession.clinicalNotes = notes;
  }

  /**
   * Get current clinical notes
   */
  getClinicalNotes(): string {
    return this.activeSession?.clinicalNotes || '';
  }

  /**
   * End and save the current session
   */
  async endSession(clinicalNotes?: string): Promise<SessionData> {
    if (!this.activeSession) {
      throw new Error('No active session to end');
    }

    // Stop recording if still active
    if (this.activeSession.isRecording) {
      throw new Error('Please stop recording before ending the session');
    }

    try {
      const session = this.activeSession;
      const endTime = new Date();
      const duration = Math.floor((endTime.getTime() - session.metadata.startTime.getTime()) / 1000);

      session.metadata.endTime = endTime;
      session.metadata.duration = duration;
      session.metadata.status = 'completed';

      if (clinicalNotes) {
        session.clinicalNotes = clinicalNotes;
      }

      // Post-session Whisper transcription (if audio available)
      let whisperTranscription: TranscriptionSession | null = null;
      if (session.audioBlob) {
        try {
          this.callbacks.onWhisperStatusUpdate?.('uploading');
          whisperTranscription = await whisperTranscriptionService.transcribe(
            session.audioBlob,
            session.metadata.sessionId,
            {
              onStatusChange: (status) => {
                this.callbacks.onWhisperStatusUpdate?.(status);
              },
            }
          );
          // Whisper transcription complete
        } catch (error) {
          console.error('Whisper transcription failed, using Web Speech transcript:', error);
        }
      }

      // Create session data — use Whisper transcript if available, fall back to Web Speech
      const sessionData: SessionData = {
        metadata: session.metadata,
        clinicalNotes: session.clinicalNotes,
        transcription: whisperTranscription || session.transcription,
        whisperTranscription: whisperTranscription || undefined,
        transcriptionSource: whisperTranscription ? 'whisper' : 'web-speech',
      };

      // Store recording if available
      if (session.audioBlob && session.filteredAudioBlob && session.recordingMetadata) {
        // Store in legacy RecordingStorage
        const recordingId = await recordingStorage.storeRecording(
          session.audioBlob,
          session.filteredAudioBlob,
          session.recordingMetadata,
          session.metadata.sessionId,
          session.metadata.patientId,
          session.clinicalNotes
        );

        // Also store in new AudioStorageService (IndexedDB)
        try {
          await audioStorageService.saveAudio(
            session.metadata.sessionId,
            session.audioBlob,
            session.filteredAudioBlob,
            {
              patientId: session.metadata.patientId,
              duration: duration,
              sampleRate: session.recordingMetadata.sampleRate || 44100,
              channels: session.recordingMetadata.channels || 1
            }
          );
        } catch (error) {
          console.error('Failed to save audio to IndexedDB:', error);
          // Don't throw - legacy storage still worked
        }

        sessionData.recordingId = recordingId;
        sessionData.recording = {
          originalBlob: session.audioBlob,
          filteredBlob: session.filteredAudioBlob,
          originalFileSize: session.audioBlob.size,
          filteredFileSize: session.filteredAudioBlob.size,
          silenceRemoved: session.recordingMetadata.silenceRemoved,
          noiseReduction: session.recordingMetadata.noiseReduction
        };
      }

      // Store filtered content if available
      if (session.transcription?.filteredSegments && session.transcription?.filteringStats) {
        sessionData.filteredContent = {
          segments: session.transcription.filteredSegments,
          stats: session.transcription.filteringStats,
          summary: this.generateFilteringSummary(session.transcription.filteringStats)
        };
      }

      // Save to session history
      this.sessionHistory.set(session.metadata.sessionId, sessionData);
      this.saveSessionHistory();

      // Note: Session data is saved to Firestore via PostSessionPipelineService
      // (session_recordings collection) — no localStorage patient record needed

      // Clear active session
      this.activeSession = null;
      this.callbacks = {};

      return sessionData;
    } catch (error) {
      console.error('Failed to end session:', error);
      this.callbacks.onError?.(`Failed to end session: ${error}`);
      throw error;
    }
  }

  /**
   * Cancel the current session without saving
   */
  async cancelSession(): Promise<void> {
    if (!this.activeSession) {
      throw new Error('No active session to cancel');
    }

    // Stop recording if active
    if (this.activeSession.isRecording) {
      await transcriptionService.stopTranscription();
      this.callbacks.onRecordingUpdate?.(false);
    }

    this.activeSession = null;
    this.callbacks = {};
  }

  /**
   * Get the active session
   */
  getActiveSession(): ActiveSession | null {
    return this.activeSession;
  }

  /**
   * Check if there's an active session
   */
  hasActiveSession(): boolean {
    return this.activeSession !== null;
  }

  /**
   * Get session by ID
   */
  getSession(sessionId: string): SessionData | null {
    return this.sessionHistory.get(sessionId) || null;
  }

  /**
   * Get all sessions for a specific patient
   */
  getPatientSessions(patientId: string): SessionData[] {
    const sessions: SessionData[] = [];

    this.sessionHistory.forEach((session) => {
      if (session.metadata.patientId === patientId) {
        sessions.push(session);
      }
    });

    return sessions.sort((a, b) =>
      b.metadata.startTime.getTime() - a.metadata.startTime.getTime()
    );
  }

  /**
   * Get all sessions for a specific provider
   */
  getProviderSessions(providerId: string): SessionData[] {
    const sessions: SessionData[] = [];

    this.sessionHistory.forEach((session) => {
      if (session.metadata.providerId === providerId) {
        sessions.push(session);
      }
    });

    return sessions.sort((a, b) =>
      b.metadata.startTime.getTime() - a.metadata.startTime.getTime()
    );
  }

  /**
   * Get all sessions
   */
  getAllSessions(): SessionData[] {
    const sessions = Array.from(this.sessionHistory.values());
    return sessions.sort((a, b) =>
      b.metadata.startTime.getTime() - a.metadata.startTime.getTime()
    );
  }

  /**
   * Delete a session
   */
  async deleteSession(sessionId: string, userId: string): Promise<boolean> {
    const session = this.sessionHistory.get(sessionId);

    if (!session) {
      return false;
    }

    try {
      // Delete recording if exists
      if (session.recordingId) {
        await recordingStorage.deleteRecording(session.recordingId, userId);
      }

      // Remove from history
      this.sessionHistory.delete(sessionId);
      this.saveSessionHistory();

      return true;
    } catch (error) {
      console.error('Failed to delete session:', error);
      return false;
    }
  }

  /**
   * Export session data
   */
  async exportSession(sessionId: string, format: 'json' | 'txt' | 'pdf'): Promise<Blob | null> {
    const session = this.sessionHistory.get(sessionId);

    if (!session) {
      return null;
    }

    try {
      if (format === 'json') {
        const exportData = {
          ...session,
          exportedAt: new Date().toISOString()
        };
        return new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      } else if (format === 'txt') {
        let text = `SESSION REPORT\n`;
        text += `${'='.repeat(50)}\n\n`;
        text += `Session ID: ${session.metadata.sessionId}\n`;
        text += `Patient ID: ${session.metadata.patientId}\n`;
        text += `Provider ID: ${session.metadata.providerId}\n`;
        text += `Session Type: ${session.metadata.sessionType}\n`;
        text += `Date: ${session.metadata.startTime.toLocaleString()}\n`;
        text += `Duration: ${this.formatDuration(session.metadata.duration)}\n\n`;

        if (session.metadata.appointmentReason) {
          text += `Reason: ${session.metadata.appointmentReason}\n\n`;
        }

        if (session.transcription) {
          text += `TRANSCRIPT\n`;
          text += `${'-'.repeat(50)}\n`;
          text += session.transcription.fullText + '\n\n';
        }

        if (session.filteredContent) {
          text += `FILTERED CONTENT SUMMARY\n`;
          text += `${'-'.repeat(50)}\n`;
          text += session.filteredContent.summary + '\n\n';
        }

        if (session.clinicalNotes) {
          text += `CLINICAL NOTES\n`;
          text += `${'-'.repeat(50)}\n`;
          text += session.clinicalNotes + '\n\n';
        }

        text += `\nExported: ${new Date().toISOString()}\n`;

        return new Blob([text], { type: 'text/plain' });
      }

      return null;
    } catch (error) {
      console.error('Failed to export session:', error);
      return null;
    }
  }

  /**
   * Get session statistics
   */
  getSessionStats(patientId?: string): {
    totalSessions: number;
    totalDuration: number;
    averageDuration: number;
    byType: Record<string, number>;
    byStatus: Record<string, number>;
    lastSessionDate?: Date;
  } {
    let sessions = Array.from(this.sessionHistory.values());

    if (patientId) {
      sessions = sessions.filter(s => s.metadata.patientId === patientId);
    }

    const stats = {
      totalSessions: sessions.length,
      totalDuration: sessions.reduce((sum, s) => sum + s.metadata.duration, 0),
      averageDuration: 0,
      byType: {} as Record<string, number>,
      byStatus: {} as Record<string, number>,
      lastSessionDate: undefined as Date | undefined
    };

    if (stats.totalSessions > 0) {
      stats.averageDuration = stats.totalDuration / stats.totalSessions;

      // Get last session date
      const sortedSessions = sessions.sort((a, b) =>
        b.metadata.startTime.getTime() - a.metadata.startTime.getTime()
      );
      stats.lastSessionDate = sortedSessions[0].metadata.startTime;
    }

    // Count by type
    sessions.forEach(session => {
      const type = session.metadata.sessionType;
      stats.byType[type] = (stats.byType[type] || 0) + 1;

      const status = session.metadata.status;
      stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;
    });

    return stats;
  }

  // Private helper methods

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  }

  private generateFilteringSummary(stats: FilteringStats): string {
    const relevantPercentage = Math.round((stats.relevantSegments / stats.totalSegments) * 100);
    const avgRelevance = Math.round(stats.averageRelevanceScore * 100);

    let summary = `Content Filtering Summary:\n\n`;
    summary += `• ${stats.relevantSegments}/${stats.totalSegments} segments (${relevantPercentage}%) marked as relevant\n`;
    summary += `• Average relevance score: ${avgRelevance}%\n`;
    summary += `• Medical content: ${stats.medicalContent} segments\n`;
    summary += `• Administrative content: ${stats.administrativeContent} segments\n`;
    summary += `• Off-topic content filtered: ${stats.offTopicContent} segments\n`;
    summary += `• Interruptions filtered: ${stats.interruptionsFiltered} segments\n`;

    return summary;
  }

  private async saveSessionToFirestore(session: SessionData): Promise<void> {
    try {
      const sessionId = session.metadata.sessionId;
      // Strip undefined values — Firestore rejects them
      const cleanMetadata: Record<string, any> = {};
      Object.entries(session.metadata).forEach(([k, v]) => {
        if (v !== undefined) cleanMetadata[k] = v;
      });
      cleanMetadata.startTime = session.metadata.startTime instanceof Date ? Timestamp.fromDate(session.metadata.startTime) : session.metadata.startTime;
      cleanMetadata.endTime = session.metadata.endTime instanceof Date ? Timestamp.fromDate(session.metadata.endTime) : (session.metadata.endTime ?? null);

      const sessionToSave = {
        metadata: cleanMetadata,
        clinicalNotes: session.clinicalNotes || '',
        recordingId: session.recordingId || null,
        tags: session.tags || [],
        transcription: session.transcription ? {
          sessionId: session.transcription.sessionId,
          fullText: session.transcription.fullText,
          startTime: session.transcription.startTime instanceof Date ? Timestamp.fromDate(session.transcription.startTime) : session.transcription.startTime,
          language: session.transcription.language,
          totalWords: session.transcription.totalWords,
          avgConfidence: session.transcription.avgConfidence,
        } : null,
        filteredContent: session.filteredContent ? {
          summary: session.filteredContent.summary,
          stats: session.filteredContent.stats,
        } : null,
        patientId: session.metadata.patientId,
        providerId: session.metadata.providerId,
        updatedAt: Timestamp.now(),
      };
      await setDoc(doc(db, 'session_history', sessionId), sessionToSave, { merge: true });
    } catch (error) {
      console.error('Failed to save session to Firestore:', error);
    }
  }

  private saveSessionHistory(): void {
    // Save each session to Firestore (fire-and-forget)
    this.sessionHistory.forEach((session) => {
      this.saveSessionToFirestore(session);
    });
  }

  private async loadSessionHistory(): Promise<void> {
    try {
      const q = query(collection(db, 'session_history'), orderBy('metadata.startTime', 'desc'));
      const snapshot = await getDocs(q);
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const session: SessionData = {
          metadata: {
            ...data.metadata,
            startTime: data.metadata.startTime instanceof Timestamp ? data.metadata.startTime.toDate() : new Date(data.metadata.startTime),
            endTime: data.metadata.endTime instanceof Timestamp ? data.metadata.endTime.toDate() : (data.metadata.endTime ? new Date(data.metadata.endTime) : undefined),
          },
          clinicalNotes: data.clinicalNotes,
          recordingId: data.recordingId,
          tags: data.tags,
          transcription: data.transcription ? {
            ...data.transcription,
            startTime: data.transcription.startTime instanceof Timestamp ? data.transcription.startTime.toDate() : new Date(data.transcription.startTime),
            segments: data.transcription.segments || [],
          } : undefined,
          filteredContent: data.filteredContent || undefined,
        };
        this.sessionHistory.set(session.metadata.sessionId, session);
      });
    } catch (error) {
      // Silenced: clients without session_history permissions will hit this — not an error
      console.debug('SessionService: could not load session history (expected for client users):', error);
    }
  }

  /**
   * Clear all session history (for testing/debugging)
   */
  async clearAllSessions(): Promise<void> {
    const snapshot = await getDocs(collection(db, 'session_history'));
    const deletes = snapshot.docs.map(d => deleteDoc(d.ref));
    await Promise.all(deletes);
    this.sessionHistory.clear();
  }
}

export const sessionService = SessionService.getInstance();
export default SessionService;
